import api from "../../Environment";
import configuration from "react-global-configuration";

/**
 * Footer Manager client.
 *
 * Fallback policy: when Footer Manager returns empty/unpublished, render
 * empty-safe columns — Organizations / Creators / Projects (no Discover
 * heading) plus optional CMS footer_pages from site config.
 * Dead labels Contact Us / Discover are stripped from presentation.
 */

/** Canonical entity landings for footer reachability (no Discover group title). */
export const FOOTER_ENTITY_LANDING_ITEMS = [
  { title: "Organizations", url: "/organizations", link_type: "internal" },
  { title: "Creators", url: "/creators", link_type: "internal" },
  { title: "Projects", url: "/projects", link_type: "internal" },
];

/** @deprecated Use FOOTER_ENTITY_LANDING_ITEMS — kept as alias for any importers. */
export const FOOTER_DISCOVER_ITEMS = FOOTER_ENTITY_LANDING_ITEMS;

function normalizeFooterTitle(title) {
  return String(title || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** Dead / placeholder labels that must not appear as active footer features. */
export function isDeadFooterLabel(title) {
  const n = normalizeFooterTitle(title);
  return n === "discover" || n === "contact us" || n === "contact";
}

/**
 * Strip dead Contact Us / Discover labels; keep valid destinations.
 * Discover section titles are cleared so Orgs/Creators/Projects stay visible.
 */
export function sanitizeFooterSections(sections) {
  const list = Array.isArray(sections) ? sections : [];
  return list
    .map((section) => {
      if (!section || typeof section !== "object") return null;
      const rawTitle = section.title || "";
      const title = isDeadFooterLabel(rawTitle) ? "" : rawTitle;
      const items = (Array.isArray(section.items) ? section.items : []).filter(
        (item) => item && !isDeadFooterLabel(item.title)
      );
      if (!items.length) return null;
      return Object.assign({}, section, { title, items });
    })
    .filter(Boolean);
}

function emptySafeFallback() {
  let pages1 = [];
  let pages2 = [];
  try {
    pages1 = configuration.get("configData.footer_pages1") || [];
    pages2 = configuration.get("configData.footer_pages2") || [];
  } catch (e) {
    /* ignore */
  }

  const toItems = (pages) =>
    (Array.isArray(pages) ? pages : [])
      .map((p) => ({
        title: p.heading,
        url: `/page/${p.unique_id}`,
        link_type: "page",
      }))
      .filter((item) => !isDeadFooterLabel(item.title));

  const left = toItems(pages1);
  const right = toItems(pages2);

  return {
    managed: false,
    empty: true,
    legacy: false,
    fallback: true,
    source: "empty_safe_fallback",
    sections: sanitizeFooterSections([
      {
        key: "entity_landings",
        column_key: "entity_landings",
        title: "",
        items: FOOTER_ENTITY_LANDING_ITEMS.slice(),
      },
      { key: "fallback_left", column_key: "left", title: "", items: left },
      { key: "fallback_right", column_key: "right", title: "", items: right },
    ]),
  };
}

export async function fetchFooterNavigation() {
  try {
    const response = await api.postMethod("footer", {});
    const body = response && response.data;
    if (body && body.success === true && body.data) {
      const data = body.data;
      if (data.empty || !data.managed) {
        return emptySafeFallback();
      }
      return {
        managed: true,
        empty: false,
        legacy: false,
        fallback: false,
        source: "footer_manager",
        sections: sanitizeFooterSections(
          Array.isArray(data.sections) ? data.sections : []
        ),
        column_keys: data.column_keys || [],
      };
    }
    // API responded without a usable managed payload — treat as load error
    // (distinct from intentional empty/unpublished managed content).
    return Object.assign(emptySafeFallback(), { loadError: true });
  } catch (e) {
    return Object.assign(emptySafeFallback(), { loadError: true });
  }
}

export function isExternalFooterUrl(url) {
  if (!url || typeof url !== "string") {
    return false;
  }
  const trimmed = url.trim();
  return /^https?:\/\//i.test(trimmed) || trimmed.startsWith("//");
}
